import type { CSSProperties } from "react";

type Props = {
    size?: number;
    className?: string;
    title?: string;
    hueRotate?: number;
    /** Perch an original folklore archer's hat on the mark. Off by default. */
    hat?: boolean;
};

/**
 * Original, hand-drawn quill feather — the wordmark flourish.
 *
 * Deliberately distinct from any corporate feather mark: ours is a
 * writing quill with a visibly curved central shaft, four separated
 * asymmetric barb segments on the leading edge, a slim translucent
 * vane on the trailing edge, and a long bare quill point sweeping to
 * the bottom-right (opposite curve direction to the well-known mark).
 * Public-domain folklore imagery, drawn from scratch.
 *
 * `tone`:
 *   - "dark": pure black, for use ON lime/light fills (brand rule:
 *     black-on-lime, never white-on-lime)
 *   - "lime": #CCFF00, for use on dark backgrounds
 *
 * Simple filled paths + one stroked shaft so it stays crisp 16–48px.
 */
export function QuillFeather({
    size = 20, tone = "lime", className, style,
}: { size?: number; tone?: "dark" | "lime"; className?: string; style?: CSSProperties }) {
    const c = tone === "dark" ? "#000000" : "#ccff00";
    return (
        <svg
            viewBox="0 0 64 64"
            width={size}
            height={size}
            className={className}
            style={{ display: "block", flexShrink: 0, ...style }}
            aria-hidden
            focusable="false"
        >
            {/* slim trailing vane, hugging the right of the shaft */}
            <path
                d="M20 7 C30 15 39 26 46 40 C41 25 32 13 22 5 Z"
                fill={c}
                fillOpacity="0.55"
            />
            {/* four separated barbs on the leading (left) edge, largest at the tip */}
            <path d="M20 8 C12 9 5 14 2 22 C10 22 17 17 22 11 Z" fill={c} />
            <path d="M26 18 C18 20 11 26 9 34 C17 32 23 26 28 21 Z" fill={c} />
            <path d="M32 28 C25 30 19 36 17 43 C24 41 30 36 34 31 Z" fill={c} />
            <path d="M39 38 C33 40 28 45 27 51 C33 49 37 45 40 41 Z" fill={c} />
            {/* curved central shaft with a long bare quill point */}
            <path
                d="M57 60 C46 50 34 34 24 20 C21 16 19 11 18 6"
                stroke={c}
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
            />
        </svg>
    );
}

/**
 * Original, hand-drawn folklore accent: a pointed archer's felt hat
 * (hood-green crown, darker turned-up brim) with a small lime feather
 * tucked behind the point. Public-domain Robin Hood folklore imagery —
 * deliberately NOT any corporate mark. Simple geometric paths so it
 * stays crisp from 16px to 48px+. Kept for the piggy mark itself (mint
 * hero); the wordmark flourish is now `QuillFeather`.
 */
export function ArcherHat({
    size = 20, className, style,
}: { size?: number; className?: string; style?: CSSProperties }) {
    const h = size * (42 / 64);
    return (
        <svg
            viewBox="0 0 64 42"
            width={size}
            height={h}
            className={className}
            style={{ display: "block", flexShrink: 0, ...style }}
            aria-hidden
            focusable="false"
        >
            {/* lime feather, tucked behind the crown point */}
            <path d="M45 26 C46 15 52 6 62 2 C61 11 56 21 48 27 Z" fill="#ccff00" />
            <path
                d="M47 25 C51 17 56 9 61 3"
                stroke="#a3cc00" strokeWidth="2" fill="none" strokeLinecap="round"
            />
            {/* pointed felt crown, swept toward the feather */}
            <path d="M10 32 C14 16 24 5 42 3 C36 12 35 22 37 32 Z" fill="#00c805" />
            {/* turned-up brim */}
            <path d="M4 33 C18 38 34 38 48 32 L46 27 C33 33 19 33 7 28 Z" fill="#008a08" />
        </svg>
    );
}

/**
 * The official Safe Piggy mark — uses the published PNG so what users
 * see on the site matches what they see on social, favicon, and NFT
 * previews.
 *
 * `hueRotate` is supported via CSS filter for the wallet-avatar use case
 * where we want a per-address color variation.
 */
export function PiggyMark({ size = 36, className, title, hueRotate, hat }: Props) {
    const img = (
        <img
            src="/piggy.png"
            alt={title ?? "Safe Piggy"}
            width={size}
            height={size}
            className={className}
            style={{
                objectFit: "contain",
                display: "block",
                ...(hueRotate ? { filter: `hue-rotate(${hueRotate}deg)` } : {}),
            }}
            draggable={false}
        />
    );

    if (!hat) return img;

    // Hat perches above the mark's top edge, tilted like it just landed.
    return (
        <span className="relative inline-block" style={{ width: size, height: size }}>
            {img}
            <ArcherHat
                size={size * 0.72}
                className="absolute pointer-events-none"
                style={{
                    top: -size * 0.26,
                    left: -size * 0.08,
                    transform: "rotate(-12deg)",
                }}
            />
        </span>
    );
}

export function SafePiggyLogo({ size = 36 }: { size?: number }) {
    return (
        <div className="flex items-center gap-2.5">
            <PiggyMark size={size} className="rounded-2xl shadow-glow" />
            <span className="display text-xl font-black text-piggy-cream tracking-tight">Safe Piggy</span>
            <QuillFeather size={size * 0.5} tone="lime" className="-ml-1 -mt-2" style={{ transform: "rotate(8deg)" }} />
        </div>
    );
}

/**
 * ETH diamond mark — inline SVG so we don't ship an extra asset.
 * `size` controls box; `className` lets callers add ring/shadow.
 */
export function EthMark({ size = 18, className }: { size?: number; className?: string }) {
    return (
        <svg
            viewBox="0 0 32 32"
            width={size}
            height={size}
            className={className}
            aria-label="ETH"
            role="img"
            style={{ display: "block", flexShrink: 0 }}
        >
            <circle cx="16" cy="16" r="16" fill="#627EEA" />
            <g fill="#fff">
                <path fillOpacity=".602" d="M16.498 4v8.87l7.497 3.35z" />
                <path d="M16.498 4 9 16.22l7.498-3.35z" />
                <path fillOpacity=".602" d="M16.498 21.968v6.027L24 17.616z" />
                <path d="M16.498 27.995v-6.028L9 17.616z" />
                <path fillOpacity=".2" d="m16.498 20.573 7.497-4.353-7.497-3.348z" />
                <path fillOpacity=".602" d="m9 16.22 7.498 4.353v-7.701z" />
            </g>
        </svg>
    );
}

/**
 * Compact ticker pill: small token logo + symbol label.
 * Used wherever PIGGY/ETH appears as a unit beside a number.
 */
export function TokenBadge({
    symbol, size = 16, className,
}: { symbol: "PIGGY" | "ETH"; size?: number; className?: string }) {
    return (
        <span className={`inline-flex items-center gap-1 align-middle ${className ?? ""}`}>
            {symbol === "PIGGY"
                ? <PiggyMark size={size} className="rounded-full" />
                : <EthMark size={size} />}
            <span>{symbol}</span>
        </span>
    );
}
